/* global __DEV__ */
import {
  initialize,
  LDClient,
  LDFlagSet,
  LDFlagValue,
  LDUser,
} from 'ldclient-js';

import { Storage } from '@trello/storage';
import {
  launchdarklyKey,
  atlassianFeatureFlagClientKey,
  environment,
} from '@trello/config';
import { memberId } from '@trello/session-cookie';
import { Analytics } from '@trello/atlassian-analytics';
import { isDesktop, isTouch } from '@trello/browser';
import AtlassianFeatureFlagClient, {
  Identifiers,
  EnvironmentType,
  AnonymousFlagUser,
  FeatureFlagUser,
} from '@atlassiansox/feature-flag-web-client';

const USER_DATA_CACHE_TIMEOUT = 24 * 60 * 60 * 1000;
const USER_DATA_CACHE_STORAGE_KEY = 'featureFlagUserData';
const OVERRIDES_STORAGE_KEY = 'featureFlagOverrides';

interface UserData {
  channel: string;
  idEnterprises: string[];
  idOrgs: string[];
  orgs: string[];
  emailDomain: string;
  clientVersion: string;
  head: string;
  version: number;
}

interface UserDataCache {
  lastUpdated: number;
  key: string;
  data: UserData;
}

interface ChangeListener<T> {
  (current?: T, previous?: T): void;
}
interface ClientListenerMap {
  [key: string]: ChangeListener<unknown>;
}

interface ChangeListenerMap {
  [key: string]: ChangeListener<unknown>[];
}

interface FeatureFlagGroups {
  remote: LDFlagSet;
  overrides: LDFlagSet;
}

export type FlagSet = LDFlagSet;

export class FeatureFlagClient {
  private user: LDUser;

  private clientListeners: ClientListenerMap = {};
  private changeListeners: ChangeListenerMap = {};
  client: LDClient;
  atlassianClient: AtlassianFeatureFlagClient | null = null;

  initializeLdClient(user: LDUser) {
    this.user = user;

    const userData: UserDataCache = Storage.get(USER_DATA_CACHE_STORAGE_KEY);
    const isUserDataExpired =
      userData && Date.now() - userData.lastUpdated > USER_DATA_CACHE_TIMEOUT;
    const sameUser = userData && userData.key === this.user.key;

    if (userData) {
      if (!isUserDataExpired && sameUser) {
        this.user = {
          ...this.user,
          custom: {
            ...this.user.custom,
            ...userData.data,
          },
        };
      } else {
        Storage.unset(USER_DATA_CACHE_STORAGE_KEY);
      }
    }

    this.client = initialize(launchdarklyKey, this.user, {
      bootstrap: 'localStorage',
      evaluationReasons: true,
      sendEventsOnlyForVariation: true,
      flushInterval: 60000,
    });
  }

  initializeAtlassianClient() {
    const identifierType = memberId
      ? Identifiers.TRELLO_USER_ID
      : Identifiers.TRELLO_ANONYMOUS_ID;
    const identifierValue = memberId;

    let clientEnv = EnvironmentType.DEV;
    if (environment === 'staging') {
      clientEnv = EnvironmentType.STAGING;
    } else if (environment === 'prod') {
      clientEnv = EnvironmentType.PROD;
    }

    // Poll at 5 minutes in prod, 5 seconds otherwise
    const pollingInterval =
      clientEnv === EnvironmentType.PROD ? 1000 * 60 * 5 : 5000;

    // Build up our flag user: we want to omit the `identifier` if the memberId
    // is not set, so that the client will generate a UUID
    const flagUser: FeatureFlagUser | AnonymousFlagUser = {};
    if (identifierValue !== null) {
      flagUser.identifier = {
        type: identifierType,
        value: identifierValue,
      };
    }

    console.log('initializing atlassian feature flag client', {
      atlassianFeatureFlagClientKey,
      identifierType,
      identifierValue,
      clientEnv,
      pollingInterval,
    });

    try {
      this.atlassianClient = new AtlassianFeatureFlagClient(
        atlassianFeatureFlagClientKey,
        { sendOperationalEvent: () => {} }, // Isnt used for this release
        flagUser,
        {
          productKey: 'trello',
          environment: clientEnv, // Would be nice if we didn't need to provide this
          pollingInterval,
        },
      );
    } catch (err) {
      console.error(
        'AtlassianFeatureFlagClient threw an error on initialisation.',
        err,
      );
    }
  }

  constructor(user: LDUser) {
    // Initialize the ldclient
    this.initializeLdClient(user);

    // Conditionally initialize the Atlassian client
    const atlassianClientFeatureFlag = 'fep.fx3-client-initialization';
    if (this.get(atlassianClientFeatureFlag, false)) {
      console.log('initializing atlassian feature flag client on startup');
      this.initializeAtlassianClient();
    }

    // Subscribe to flag changes and initialize or tear down
    // the Atlassian client
    this.on(atlassianClientFeatureFlag, false, (newValue) => {
      if (newValue && !this.atlassianClient) {
        console.log('initializing atlassian feature flag client at runtime');
        this.initializeAtlassianClient();
      } else if (!newValue && this.atlassianClient) {
        console.log('destroying atlassian feature flag client');
        this.atlassianClient?.destroy();
        this.atlassianClient = null;
      }
    });
  }

  refineUserData(data: UserData) {
    const cacheData: UserDataCache = {
      lastUpdated: Date.now(),
      key: this.user.key || 'notLoggedIn',
      data,
    };

    Storage.set(USER_DATA_CACHE_STORAGE_KEY, cacheData);

    return this.client.identify({
      ...this.user,
      custom: {
        ...this.user.custom,
        ...data,
      },
    });
  }

  get = <T>(key: string, defaultValue: T) => {
    if (defaultValue === undefined && __DEV__) {
      console.warn(
        `Flag evaluation now requires a default value as the second argument to .get(). Please ensure a default value as the second argument when evaluating ${key}`,
      );
    }
    const override = this.getOverride(key);

    if (typeof override !== 'undefined') {
      return override;
    }

    return this.client.variation(key, defaultValue);
  };

  getTrackedVariation = <T>(
    key: string,
    defaultValue: T,
    attributes?: object,
  ) => {
    const override = this.getOverride(key);

    if (typeof override !== 'undefined') {
      FeatureFlagClient.triggerExposureEvent(key, override, 'OVERRIDE');
      return override;
    }

    const variationDetail = this.client.variationDetail(key, defaultValue);
    const { value, reason } = variationDetail;
    const { kind, ruleId } = reason;
    FeatureFlagClient.triggerExposureEvent(
      key,
      value,
      kind,
      ruleId,
      attributes,
    );
    return value;
  };

  all(): FeatureFlagGroups {
    const remote = this.client.allFlags();
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY) || {};

    return {
      remote,
      overrides,
    };
  }

  public static triggerExposureEvent(
    flagKey: string,
    value: string,
    reason: string,
    ruleId?: string,
    attributes?: object,
  ) {
    Analytics.sendTrackEvent({
      source: '@trello/feature-flag-client',
      actionSubject: 'feature',
      action: 'exposed',
      attributes: {
        flagKey,
        reason,
        ruleId,
        value,
        ...attributes,
      },
    });
  }

  private getClientListener(key: string): ChangeListener<unknown> {
    if (Object.prototype.hasOwnProperty.call(this.clientListeners, key)) {
      return this.clientListeners[key];
    }

    this.clientListeners[key] = (current, previous) => {
      if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
        if (typeof this.getOverride(key) === 'undefined') {
          for (const listener of this.changeListeners[key]) {
            listener(current, previous);
          }
        } else {
          console.warn(
            `Ignoring feature flag change event for "${key}" from server due to local override`,
          );
        }
      }
    };

    return this.clientListeners[key];
  }

  on = <T>(key: string, defaultValue: T, callback: ChangeListener<T>) => {
    if (typeof defaultValue === 'function' && __DEV__) {
      console.warn(
        `Flag subscription now requires a default value as the second argument to .on(). Please ensure a default value as the second argument when subscribing to ${key}`,
      );
    }
    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      this.changeListeners[key].push(callback);
    } else {
      this.changeListeners[key] = [callback];
      this.client.on(`change:${key}`, this.getClientListener(key));
    }
  };

  off = (key: string, callback: ChangeListener<unknown>) => {
    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      const idx = this.changeListeners[key].indexOf(callback);

      if (idx > -1) {
        this.changeListeners[key].splice(idx, 1);
      }

      if (this.changeListeners[key].length === 0) {
        this.client.off(`change:${key}`, this.getClientListener(key));
        delete this.clientListeners[key];
      }
    }
  };

  private getOverride(key: string): LDFlagValue {
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY);

    if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key];
    }
  }

  setOverride<T>(key: string, value: T): void {
    // We assume false as the default value here, as our overriding doesn't currently
    // support anything other than booleans
    const originalValue = this.get(key, false);
    const existingOverrides: LDFlagSet =
      Storage.get(OVERRIDES_STORAGE_KEY) || {};

    Storage.set(OVERRIDES_STORAGE_KEY, {
      ...existingOverrides,
      [key]: value,
    });

    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      // Call all change listeners for this feature flag
      for (const listener of this.changeListeners[key]) {
        listener(value, originalValue);
      }
    }
  }

  resetOverrides(): void {
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY) || {};
    Storage.set(OVERRIDES_STORAGE_KEY, {});

    // Call change listeners for all overrides that were just reset
    Object.entries(overrides).forEach(([key, originalValue]) => {
      if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
        for (const listener of this.changeListeners[key]) {
          listener(this.get(key, false), originalValue);
        }
      }
    });
  }
}

export const featureFlagClient = new FeatureFlagClient({
  key: memberId || 'notLoggedIn',
  anonymous: !memberId,
  custom: {
    isDesktop: isDesktop(),
    isTouch: isTouch(),
  },
});
